import { i18nNsFactory } from '@atlas-frontend/i18n';
import { TextLink } from '../../../common/components/typography';
import translations from './AlgorithmMappings.translations.json';
import React, { FC, MouseEventHandler, useMemo } from 'react';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { Table } from '../../../common/tanstacktable';
import { TextEllipsis } from '../../../common/components';
import { useSystemClassifiers } from '../../../configuration/useSystemClassifiers';
import { expand } from 'uri-template-lite';
import { ClassifierMappingDetailsUrl } from '../../../classifier-mapping';
import styled from 'styled-components';
import { colorBgContainer, colorPurple } from '@atlas-frontend/design-tokens';
import type { AlgorithmMappingQueryDtoType } from './schema/types';
import { AlgorithmHeader } from '../AlgorithmHeader';
import { getAlgorithmSourceShortLabel } from '../utils';

const { addTranslations, useTranslation } = i18nNsFactory('algorithm-mappings');
addTranslations(translations);

const columnHelper = createColumnHelper<AlgorithmMappingQueryDtoType>();

export interface AlgorithmMappingsProps {
  algorithmId: number;
  onEditButtonClick?: MouseEventHandler<HTMLButtonElement>;
  onEditGraphClick?: MouseEventHandler<HTMLButtonElement>;
  editGraphLabel?: string;
  expanded?: boolean;
  onToggle?: (expanded: boolean) => void;
  hasError?: boolean;
  mappings?: AlgorithmMappingQueryDtoType[];
  isLoading?: boolean;
}

export const AlgorithmMappings: FC<AlgorithmMappingsProps> = (props) => {
  const { algorithmId, mappings, isLoading, ...rest } = props;
  const { t } = useTranslation();

  const {
    MAPPING_JOIN_TYPE: { systemClassifierItems: mappingTypes },
  } = useSystemClassifiers('MAPPING_JOIN_TYPE');

  const columns: ColumnDef<AlgorithmMappingQueryDtoType, any>[] =
    useMemo(() => {
      return [
        columnHelper.accessor('sourceDimension', {
          header: t('column.sourceDimension'),
          enableColumnFilter: false,
          enableSorting: false,
          cell: (props) => {
            const mapping = props.row.original;
            const sourceValue = getAlgorithmSourceShortLabel({
              alias: mapping.algorithmSourceAlias || undefined,
              sourceCode:
                mapping.dataObjectDescriptionCode ||
                mapping.dataSourceCode ||
                '',
              numberOfPreviousPeriod:
                mapping.algorithmSourcePeriodOffset || undefined,
            });
            return (
              <TextEllipsis
                strong
              >{`${sourceValue}.${props.renderValue()}`}</TextEllipsis>
            );
          },
          meta: {
            width: 100,
            className: 'hide-box-shadow',
          },
        }),
        columnHelper.accessor('classifierMappingCode', {
          header: t('column.classifierMappingCode'),
          enableColumnFilter: false,
          enableSorting: false,
          cell: (props) => {
            const {
              row: {
                original: {
                  classifierMappingCode,
                  classifierMappingName,
                  classifierMappingId,
                },
              },
            } = props;

            return (
              <TextLink
                id={`classifier-mapping-${props.row.index}`}
                href={expand(ClassifierMappingDetailsUrl, {
                  id: classifierMappingId,
                })}
                target="_blank"
              >
                <TextEllipsis strong>
                  {`${classifierMappingCode} - ${classifierMappingName}`}
                </TextEllipsis>
              </TextLink>
            );
          },
          meta: {
            width: 100,
            className: 'hide-box-shadow',
          },
        }),
        columnHelper.accessor('joinTypeCode', {
          header: t('column.joinTypeCode'),
          enableColumnFilter: false,
          enableSorting: false,
          cell: (props) => (
            <TextEllipsis strong>
              {mappingTypes?.find((item) => item.code === props.renderValue())
                ?.name || ''}
            </TextEllipsis>
          ),
          meta: {
            width: 60,
            className: 'hide-box-shadow',
          },
        }),
        columnHelper.accessor('alias', {
          header: t('column.alias'),
          enableColumnFilter: false,
          enableSorting: false,
          cell: (props) => (
            <AliasTextEllipsis strong>{props.renderValue()}</AliasTextEllipsis>
          ),
          meta: {
            width: 80,
            className: 'hide-box-shadow',
          },
        }),
      ];
    }, [mappingTypes, t]);

  return (
    <AlgorithmHeader
      algorithmId={algorithmId}
      id="algorithm-mappings"
      title={t('title')}
      count={isLoading ? undefined : mappings?.length}
      isLoading={isLoading ? 'manage' : undefined}
      hideExpand={!mappings?.length}
      {...rest}
    >
      {Boolean(mappings?.length) && (
        <TableWrapper id="mappings-view-table-wrapper">
          <Table
            name="mappings-view-table"
            data={mappings || []}
            columns={columns}
            sticky
            scrollContainerSelector="#mappings-view-table-wrapper"
          />
        </TableWrapper>
      )}
    </AlgorithmHeader>
  );
};

const TableWrapper = styled.div`
  overflow: auto;
  max-height: 400px;

  th {
    background-color: ${colorBgContainer};

    &::before {
      display: none;
    }
  }
`;

const AliasTextEllipsis = styled(TextEllipsis)`
  color: ${colorPurple[5]};
`;
